// Lib
import { flow, filter, take, sortBy, trim, reduce, isEmpty } from 'lodash/fp';
import memoizeOne from 'memoize-one';

// Utils
import { QueryMode, prepareQuery } from '../../../../common/utils/getQueryRegex';

// Constants
import { BOARD_DEFAULT_TITLE } from '../../../../common/boards/boardConstants';

const RECENTLY_VIEWED_BOARDS_LIMIT = 20;
const MAX_QUICK_SEARCH_RESULTS = 5;

// Score weights
const PERFECT_MATCH_SCORE_WEIGHT = 1000;
const MATCHING_WORDS_SCORE_WEIGHT = 50;
const RECENTLY_VIEWED_SCORE_WEIGHT = 20;

export const scoreDescending = (match) => match.score * -1;
export const sortByScoreDescending = sortBy(scoreDescending);

const mostRecentlyViewedWithoutCurrentBoard = memoizeOne((currentBoardId, viewedBoardIds) =>
    flow(
        filter((boardId) => boardId !== currentBoardId),
        take(RECENTLY_VIEWED_BOARDS_LIMIT),
    )(viewedBoardIds),
);

const calculateScore = ({ query, perfectQueryRegEx, recentlyViewedBoardIds, boardSummary, matchingWords }) => {
    const { id, title } = boardSummary;
    let score = 0;

    if (isEmpty(title)) return score;

    // Boost score if perfect match
    if (title.match(perfectQueryRegEx)) {
        score += PERFECT_MATCH_SCORE_WEIGHT;
    }

    // Boost score per matching word (minus distance)
    matchingWords.forEach((matchingWord) => {
        const distance = Math.abs(query.indexOf(matchingWord) - title.indexOf(matchingWord));
        score += MATCHING_WORDS_SCORE_WEIGHT - distance;
    });

    // Add score based on recently viewed boards
    const recentlyViewedIndex = recentlyViewedBoardIds.indexOf(id);
    const isRecentlyViewed = recentlyViewedIndex !== -1;
    const recentlyViewedScore = isRecentlyViewed
        ? (recentlyViewedBoardIds.length - recentlyViewedIndex) / recentlyViewedBoardIds.length
        : 0;

    score += recentlyViewedScore * RECENTLY_VIEWED_SCORE_WEIGHT;

    return score;
};

const getBoardMatch =
    ({ query, queryRegEx, perfectQueryRegEx, recentlyViewedBoardIds, currentBoardId }) =>
    (acc, boardSummary) => {
        const { id, title } = boardSummary;

        // Filter out current or new board or board with empty title
        const isCurrentOrNewBoard = id === currentBoardId || title === BOARD_DEFAULT_TITLE;
        if (isEmpty(title) || isCurrentOrNewBoard) return acc;

        // Filter out boards without matching words
        const matchingWords = title.match(queryRegEx);
        const hasNoMatchingWords = !Array.isArray(matchingWords) || matchingWords.length === 0;
        if (hasNoMatchingWords) return acc;

        // Filter out boards that is below MIN_SCORE
        const score = calculateScore({ query, perfectQueryRegEx, recentlyViewedBoardIds, boardSummary, matchingWords });
        acc.push({
            id,
            matchElementIds: [id],
            score,
        });

        return acc;
    };

let boardSummaries = [];

export const initQuickSearch = (newBoardSummaries) => {
    boardSummaries = newBoardSummaries;
};

export const quickSearch = ({ query, viewedBoardIds, currentBoardId }) => {
    const trimmedQuery = trim(query);
    if (trimmedQuery === '') {
        return [];
    }

    // RegExp to find per word matches
    const queryRegEx = new RegExp(`(${prepareQuery(trimmedQuery, QueryMode.PHRASE)})`, 'gi');

    // RegExp to find perfect matches
    const perfectQueryRegEx = new RegExp(`^(${query})$`, 'gi');

    // Don't include the current board within the searched boards
    const recentlyViewedBoardIds = mostRecentlyViewedWithoutCurrentBoard(currentBoardId, viewedBoardIds);

    return flow(
        reduce(getBoardMatch({ query, perfectQueryRegEx, queryRegEx, recentlyViewedBoardIds, currentBoardId }), []),
        sortBy(scoreDescending),
        take(MAX_QUICK_SEARCH_RESULTS),
    )(boardSummaries);
};
